import type { StaffImportProgress } from "@lafayette/schemas";
import { useState } from "react";
import { actionRow, hintText } from "../../../catalog/components/styles";
import { Button } from "../../../ui/button";
import { Notice } from "../../../ui/notice";
import { SectionCard } from "../../../ui/section-card";
import { StatusMessage } from "../../../ui/status-message";
import type { SaveMessage } from "../../commands";
import { describeReviewFailure, type ReviewCommands } from "../commands";
import { intentStateLabels, jobFailureMessages, plural } from "../labels";
import { canRetryJob, processingReconciliation } from "../progress";
import { ToneBadge } from "./parts";

type ProgressViewProps = Readonly<{
  progress: StaffImportProgress;
  refreshing: boolean;
  onRefresh: () => void;
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
  /** The retry made a new job: open it. */
  onRetried: (jobId: string) => void;
}>;

/**
 * What the server did with the approved rows: how many were created, updated, skipped, invalid or failed.
 * The counters are a polite live region, so a screen reader hears the progress. The numbers add up to the
 * row count, and the page says so (or says that they do not). Retry is offered only for a failed job.
 */
export function ProgressView({
  progress,
  refreshing,
  onRefresh,
  commands,
  onMessage,
  onRetried,
}: ProgressViewProps) {
  const { job, processing, intent } = progress;
  const reconciliation = processingReconciliation(progress);
  const finished = job.status === "COMPLETED" || job.status === "FAILED";
  const done = reconciliation.sum - processing.waiting - processing.retrying;
  const [retryError, setRetryError] = useState<string | null>(null);

  async function retry() {
    setRetryError(null);
    onMessage(null);
    const outcome = await commands.retry({ expectedVersion: commands.jobVersion() ?? job.version });
    if (outcome.ok) {
      onRetried(outcome.data.job.id);
      return;
    }
    setRetryError(describeReviewFailure(outcome));
  }

  const summary = !finished
    ? job.status === "APPROVED"
      ? "Approved. Waiting for the server to start."
      : `Processing: ${done} of ${reconciliation.total} rows are done.`
    : job.status === "COMPLETED"
      ? `The import finished. ${plural(reconciliation.total, "row was", "rows were")} handled.`
      : job.failureCode === null
        ? "The import failed."
        : jobFailureMessages[job.failureCode];

  return (
    <SectionCard id="import-progress" title="Import progress">
      <p role="status" className="m-0 font-semibold">
        {summary}
      </p>
      <ul
        className="m-0 grid list-none gap-2 p-0 [grid-template-columns:repeat(auto-fit,minmax(9.5rem,1fr))]"
        aria-label="Rows by result"
      >
        {reconciliation.parts.map((part) => (
          <li key={part.key} className="grid gap-1 rounded-sm border border-border bg-card p-4">
            <span data-count="" className="text-3xl leading-none font-bold">
              {part.count}
            </span>
            <ToneBadge view={part.view} />
          </li>
        ))}
      </ul>
      {reconciliation.reconciled ? (
        <p className={hintText} role="status">
          <span aria-hidden="true">✓ </span>
          {plural(reconciliation.total, "row", "rows")} in total. Every row is counted once.
        </p>
      ) : (
        <Notice tone="warning" role="status">
          The counts add up to {reconciliation.sum}, but the import has{" "}
          {plural(reconciliation.total, "row", "rows")}. Refresh the page.
        </Notice>
      )}
      {intent === null ? null : (
        <p className={hintText}>
          Background task: {intentStateLabels[intent.state].toLowerCase()} (try {intent.attempts} of{" "}
          {intent.maxAttempts}).
        </p>
      )}
      <div className={actionRow}>
        {finished ? null : (
          <Button type="button" variant="secondary" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? "Refreshing…" : "Refresh now"}
          </Button>
        )}
        {canRetryJob(job) ? (
          <Button type="button" disabled={commands.pending} onClick={() => void retry()}>
            {commands.pending ? "Working…" : "Retry import"}
          </Button>
        ) : null}
      </div>
      {canRetryJob(job) ? (
        <p className={hintText}>
          A retry starts a new import on the same file. Rows that were imported are skipped. Your
          decisions are kept.
        </p>
      ) : null}
      <StatusMessage message={retryError === null ? null : { tone: "error", text: retryError }} />
    </SectionCard>
  );
}
